import { Button, InlayPanel } from '@sorrem-client/ui/components';
import { useCommission } from '@sorrem-client/ui/hooks';
import { isCommissionGoalVisible, type CharacterCommission } from '@sorrem/shared';
import styles from './CommissionCard.module.scss';

interface CommissionCardProps {
    commission: CharacterCommission;
    isAccepting: boolean;
    onAccept: () => void;
}

const STATUS_LABELS: Record<CharacterCommission['status'], string> = {
    offered: 'Offered',
    accepted: 'In progress',
    completed: 'Complete',
};

/**
 * One commission in the Overview's commission list: its issuer and goals,
 * with an Accept (or Return) button until it's complete. Renders nothing
 * until its content has loaded.
 */
export function CommissionCard({ commission, isAccepting, onAccept }: CommissionCardProps): JSX.Element | null {
    const { commission: content } = useCommission(commission.commissionId);
    if (content === null) {
        return null;
    }
    const isComplete = commission.status === 'completed';

    return (
        <InlayPanel className={styles.commissionCard}>
            <div className={styles.header}>
                <span className={styles.issuer}>{content.issuer}</span>
                <span className={isComplete ? styles.statusComplete : styles.status}>{STATUS_LABELS[commission.status]}</span>
            </div>
            <ul className={styles.objectives}>
                {/* Goals hidden until they unlock in the run stay hidden here. */}
                {commission.goals.filter((goal) => isCommissionGoalVisible(goal, undefined)).map((goal) => (
                    <li key={goal.id} className={styles.objective}>{goal.description}</li>
                ))}
            </ul>

            {!isComplete && (
                <Button variant="primary" size="small" onClick={onAccept} disabled={isAccepting}>
                    {commission.status === 'accepted' ? 'Return to the Crucible' : 'Accept Commission'}
                </Button>
            )}
        </InlayPanel>
    );
}
